import { useEffect } from "react";

import mapboxgl from "mapbox-gl";
import { useMapStore } from "stores/map";

interface ScreenshotCamera {
  readonly center: mapboxgl.LngLat;
  readonly zoom: number;
  readonly bearing: number;
  readonly pitch: number;
}

export interface UsePreScreenshotViewParams {
  readonly mapbox: Maybe<mapboxgl.Map>;
  /** True while the capture frame is on screen. */
  readonly isComposing: boolean;
}

/**
 * The map keeps behaving normally while a screenshot is composed — panning still
 * adds and drops wells — so the view the user had when they entered is saved and
 * put back on the way out: the extent directly, so no filter widened while
 * composing outlives the composition, and the camera by `jumpTo`, which discards
 * any pan done inside screenshot mode.
 *
 * The restore is the effect's cleanup, not a branch on `isComposing` turning
 * false. The extent is the global well filter, and a composition can end
 * without that transition — a route change, a project switch, the dashboard
 * tearing down, a crash — in which case a branch would leave the widened filter
 * behind with nothing to attribute it to. Cleanup runs on every one of those.
 * It also means the entry view is captured once per composition, in the
 * closure, so a re-render while composing cannot overwrite it with the
 * already-widened view. If the map instance itself is replaced mid-composition,
 * the old instance is restored and the new one's view becomes the entry view.
 */
export function usePreScreenshotView({
  mapbox,
  isComposing
}: UsePreScreenshotViewParams): void {
  const setMapExtent = useMapStore((state) => state.setMapExtent);

  useEffect(() => {
    if (!mapbox || !isComposing) {
      return;
    }
    const camera: ScreenshotCamera = {
      center: mapbox.getCenter(),
      zoom: mapbox.getZoom(),
      bearing: mapbox.getBearing(),
      pitch: mapbox.getPitch()
    };
    const extent = useMapStore.getState().mapExtent;
    return () => {
      setMapExtent(extent);
      mapbox.jumpTo(camera);
    };
  }, [isComposing, mapbox, setMapExtent]);
}
